import { useTranslation } from 'react-i18next';
import EmptyListResult from '../../components/EmptyListResult/EmptyListResult';
import { ActionIcon, Badge, Checkbox, Grid, Skeleton, Text, TextInput, Tooltip, rem } from '@mantine/core';
import { useHistory } from 'react-router-dom';
import {
  IconBrandWhatsapp,
  IconCurrencyDollar,
  IconEdit,
  IconMail,
  IconPhoto,
  IconPng,
  IconPrinter,
  IconQrcode,
  IconReceiptDollar,
  IconShirt,
  IconTrash,
  IconUpload,
  IconWash
} from '@tabler/icons-react';
import { useState } from 'react';
import CatalogCard from '../../components/CatalogCard/CatalogCard';
import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroll-component';
import { formatMoneyString, getCustomerFullName, getWhatsappURLString } from '../../utils/strings';
import { IOrder } from '../../interfaces/Order';
import OrderStatuses from '../../enum/OrderStatuses';

import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { useSelector } from 'react-redux';
import OrdersCardMoreActionsMenu from '../OrdersCardMoreActionsMenu';
import PrintTicketDrawer from '../PrintTicketDrawer';
import { buildOrderFolio, getOrderCardColorsByAmountDueAndStatus, isOrderPartiallyCollected } from '../../utils/ordersUtils';
import { IStore } from '../../interfaces/Store';
import CardLoaderSkeletonGridRows from '../Skeletons/CardLoaderSkeletonGridRows';
import { getPiecesBreakdown, formatPiecesBreakdown } from '../../utils/helpers/getAllPieces';
import { getOrderAmountDue } from '@washday/utilities';
import { useDisclosure } from '@mantine/hooks';
import useCanEditOrders from '../../hooks/useCanEditOrders';

const CleaningOrderCards = ({
  searchParamStoreId,
  isLoadingOrders,
  onOrderCleaned,
  onOrderCancelled,
  onSendEmailReceipt,
  onDownloadPNGTicket,
  showPaymentLinesModal,
  showOrderProductsModal,
  orders = [],
  fetchNextPage = () => {},
  hasNextPage = false,
  onShowCreateOrderEvidenceModal,
  onShowOrderEvidenceModal
}: {
  orders: any;
  fetchNextPage: any;
  hasNextPage: boolean;
  searchParamStoreId: string;
  isLoadingOrders: boolean;
  onOrderCleaned: ({
    sequence,
    storeId,
    params
  }: {
    sequence: string;
    storeId: string;
    params: {
      markOutsourcedAsReturned?: boolean;
    };
  }) => Promise<void>;
  onOrderCancelled: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  onSendEmailReceipt: (orderId: string) => Promise<void>;
  onPrintTicket: (order: IOrder) => Promise<void>;
  onDownloadPNGTicket: (order: IOrder) => Promise<void>;
  showPaymentLinesModal: (order: IOrder) => void;
  showOrderProductsModal: (order: IOrder) => void;
  onShowCreateOrderEvidenceModal: (order: IOrder) => void;
  onShowOrderEvidenceModal: (order: IOrder) => void;
}) => {
  const { t } = useTranslation([
    'FieldName',
    'CommonButtons',
    'Notifications',
    'OrdersPage',
    'CleaningPage',
    'UpdateOrderForm',
    'ReportsPage',
    'OrdersHeaderSummary',
    'NewOrder'
  ]);
  const operationalReturnLabel = t('NewOrder:operationalReturns.badge');
  const history = useHistory();
  const { company } = useSelector((state) => state.user);
  const canEditOrders = useCanEditOrders();
  const [openedPrintTicketDrawer, { open: openPrintTicketDrawer, close: closePrintTicketDrawer }] =
    useDisclosure(false);
  const [printTicketOrder, setPrintTicketOrder] = useState<IOrder | null>(null);

  const handleOpenPrintTicketDrawer = (order: IOrder) => {
    setPrintTicketOrder(order);
    openPrintTicketDrawer();
  };

  const handleClosePrintTicketDrawer = () => {
    closePrintTicketDrawer();
    setPrintTicketOrder(null);
  };

  return (
    <>
      <PrintTicketDrawer
        opened={openedPrintTicketDrawer}
        onClose={handleClosePrintTicketDrawer}
        order={printTicketOrder}
      />
      {isLoadingOrders && <CardLoaderSkeletonGridRows cardsQty={3} cardContentRows={8} />}
      {orders && orders.length === 0 && <EmptyListResult />}
      <InfiniteScroll
        dataLength={orders?.length || 0}
        next={fetchNextPage}
        hasMore={hasNextPage}
        style={{ overflow: 'hidden', position: 'relative' }}
        loader={
          <>
            <CardLoaderSkeletonGridRows cardsQty={3} cardContentRows={8} />
          </>
        }
        // endMessage={<Empty description={false} />}
      >
        <>
          <Grid gutter="md" pos={'relative'}>
            {orders?.map((order: IOrder) => {
              const orderProducts = order?.products || [];
              const orderBuyAndGerProducts = order?.buyAndGetProducts || [];
              const allPieces = formatPiecesBreakdown(getPiecesBreakdown(orderProducts, orderBuyAndGerProducts), {
                measurementUnits: (order.createdIn as IStore)?.measurementUnits
              });
              const amountDue = getOrderAmountDue(order);
              const customColors = getOrderCardColorsByAmountDueAndStatus({
                amountDue,
                highlightOrderCardsByPaymentPending: (order.createdIn as Partial<IStore>)
                  ?.ordersDisplayConfig?.highlightOrderCardsByPaymentPending,
                status: order.status
              });
              return (
                <Grid.Col key={order._id} span={{ xs: 12, sm: 6, lg: 4 }}>
                  <CatalogCard
                    fields={[
                      {
                        label: t('FieldName:pieces'),
                        value: allPieces
                      },
                      {
                        label: t('FieldName:name'),
                        value: getCustomerFullName(order.customer.name, order.customer.lastName)
                      },
                      {
                        label: t('FieldName:total'),
                        value: formatMoneyString((order.total || 0).toFixed(2))
                      },
                      {
                        label: t('FieldName:amountDue'),
                        value: formatMoneyString(amountDue.toFixed(2))
                      },
                      {
                        label: t('FieldName:status'),
                        value: (
                          <>
                            {t(`FieldName:orderStatuses.${OrderStatuses[order.status.toUpperCase()]}`)}
                            {isOrderPartiallyCollected(order) && (
                              <Badge component="span" color="orange" size="xs" variant="light" ml={4}>
                                {t('FieldName:partialDeliveryBadge')}
                              </Badge>
                            )}
                          </>
                        )
                      },
                      {
                        label: t('FieldName:createdDate'),
                        value: moment(order.createdDate).format('DD/MM/YYYY HH:mm') || ''
                      },
                      {
                        label: t('FieldName:deliveryDate'),
                        value: moment(order.deliveryInfo?.date).format('DD/MM/YYYY') || ''
                      },
                      {
                        label: t('FieldName:notes'),
                        value: order.notes || '',
                        valueTooltip: order.notes
                        // lineClamp: 5
                      }
                    ]}
                    cardActionButtons={[
                      {
                        action: () => onShowCreateOrderEvidenceModal(order),
                        disabled: false,
                        Icon: <IconUpload />,
                        tooltip: t('OrdersPage:evidenceModal.uploadTooltip')
                      },
                      {
                        action: () => showPaymentLinesModal(order),
                        disabled: false,
                        Icon: <IconCurrencyDollar />,
                        tooltip: t('OrdersPage:paymentLinesModal.tooltip'),
                        testId: `order-action-payment-lines-${order._id}`
                      },
                      {
                        action: () => showOrderProductsModal(order),
                        disabled: false,
                        Icon: <IconShirt />,
                        tooltip: t('OrdersPage:productsModal.tooltip')
                      },
                      {
                        action: () =>
                          history.push(`/orders/${order._id}?storeId=${searchParamStoreId}`, {
                            storeID: searchParamStoreId
                          }),
                        disabled: !canEditOrders,
                        Icon: <IconEdit />,
                        tooltip: t('CommonButtons:edit')
                      },
                      {
                        action: () => {
                          let markOutsourcedAsReturnedValue =
                            company?.companyServicesPermissions?.enableOutsourcing; // Variable para capturar el valor del checkbox
                          return ConfirmationModal({
                            title: t('Notifications:CRUD.cleanedConfirmation'),
                            onConfirm: () =>
                              onOrderCleaned({
                                sequence: order.sequence || '',
                                storeId: order?.createdIn?._id || '',
                                params: {
                                  markOutsourcedAsReturned: markOutsourcedAsReturnedValue
                                }
                              }),
                            onCancel: () => {},
                            children: company?.companyServicesPermissions?.enableOutsourcing ? (
                              <Checkbox
                                defaultChecked={markOutsourcedAsReturnedValue}
                                label={t('Notifications:CRUD.markOutsourcedReturned')}
                                onChange={(e) => {
                                  markOutsourcedAsReturnedValue = e.target.checked;
                                }}
                              />
                            ) : undefined
                          });
                        },
                        disabled: false,
                        Icon: <IconWash />,
                        tooltip: 'Pedido Procesado',
                        testId: `order-action-mark-ready-${order._id}`
                      },
                      {
                        action: () =>
                          ConfirmationModal({
                            title: t('Notifications:CRUD.cancelConfirmation'),
                            onConfirm: () =>
                              onOrderCancelled({
                                sequence: order.sequence || '',
                                storeId: order.createdIn?._id || ''
                              }),
                            onCancel: () => {}
                          }),
                        disabled: false,
                        Icon: <IconTrash />,
                        tooltip: 'Cancelar Pedido'
                      }
                    ]}
                    cardTitle={buildOrderFolio(order.folioPrefix, order.sequence) || ''}
                    cardTestId={`order-card-${order._id}`}
                    customColors={customColors}
                    cardTitleExtra={
                      <>
                        {order.orderType === 'return' ? (
                          <Tooltip label={operationalReturnLabel} withinPortal={false}>
                            <Badge
                              color="blue"
                              mr={4}
                              size="xs"
                              style={{ flexShrink: 0 }}
                              variant="filled">
                              {operationalReturnLabel}
                            </Badge>
                          </Tooltip>
                        ) : null}
                        {(order?.evidence?.length ?? 0) > 0 && (
                          <Tooltip
                            withinPortal={false}
                            label={t('OrdersPage:evidenceModal.viewTooltip')}>
                            <ActionIcon
                              color={customColors.headerTextColor}
                              onClick={() => onShowOrderEvidenceModal(order)}
                              variant="transparent"
                              aria-label={t('OrdersPage:evidenceModal.viewTooltip')}>
                              <IconPhoto />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip withinPortal={false} label="Imprimir Ticket">
                          <ActionIcon
                            color={customColors.headerTextColor}
                            onClick={() => handleOpenPrintTicketDrawer(order)}
                            variant="transparent"
                            aria-label="Imprimir Ticket">
                            <IconPrinter />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip
                          withinPortal={false}
                          label={
                            order.customer?.phone
                              ? t('CommonButtons:sendWhatsappMessage')
                              : t('CommonButtons:noCustomerPhone')
                          }>
                          <ActionIcon
                            color={customColors.headerTextColor || 'green'}
                            disabled={!order.customer.phone}
                            onClick={() => {
                              window.open(
                                getWhatsappURLString(
                                  order.customer?.phone,
                                  order.customer?.phoneCountryCode
                                ),
                                '_blank'
                              );
                            }}
                            variant="transparent"
                            aria-label={
                              order.customer?.phone
                                ? t('CommonButtons:sendWhatsappMessage')
                                : t('CommonButtons:noCustomerPhone')
                            }>
                            <IconBrandWhatsapp />
                          </ActionIcon>
                        </Tooltip>
                        <OrdersCardMoreActionsMenu order={order} customColors={customColors} />
                      </>
                    }
                    TitleIcon={IconReceiptDollar}
                  />
                </Grid.Col>
              );
            })}
          </Grid>
        </>
      </InfiniteScroll>
    </>
  );
};

export default CleaningOrderCards;
